import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Mail, Send } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { Marquee } from "@/components/site/Marquee";
import { SectionHead } from "@/components/site/SectionHead";
import { Reveal } from "@/components/site/Reveal";
import { ProcessSteps } from "@/components/site/ProcessSteps";
import { FaqList, faqLd } from "@/components/site/FaqList";
import { InquiryForm } from "@/components/site/InquiryForm";
import { CtaBand } from "@/components/site/CtaBand";
import { StatsBand } from "@/components/site/StatsBand";
import { LogoSlider } from "@/components/site/LogoSlider";
import { TestimonialSlider } from "@/components/site/TestimonialSlider";
import { PortfolioPreview } from "@/components/site/PortfolioPreview";
import { PAGE_SEO } from "@/data/seo";
import { webPageLd } from "@/lib/schema";
import { Hero } from "./home/Hero";
import { Challenges, CoreServices, IndustriesGrid, Functions, Software, Why, WhoWeHelp } from "./home/Sections";
import { HOME_FAQS, WHO } from "@/data/home";

const MARQUEE = ["Gyms", "Yoga Studios", "Sports Academies", "Swimming Clubs", "Martial Arts", "Dance Studios", "Spas", "Personal Trainers", "Wellness Centres", "Sports Events"];

const STEPS = [
  { icon: Send, t: "Fill in the form", d: "Name, email and phone are all we need to start." },
  { icon: Mail, t: "We receive it instantly", d: "Your inquiry goes straight to our team." },
  { icon: MessageCircle, t: "We get back to you", d: "Expect a reply with next steps and questions." },
];

export default function Home() {
  return (
    <>
      <Seo
        title={PAGE_SEO["/"].title}
        description={PAGE_SEO["/"].description}
        path="/"
        jsonLd={[webPageLd({ name: PAGE_SEO["/"].title, description: PAGE_SEO["/"].description, path: "/" }), faqLd(HOME_FAQS)]}
      />
      <Hero />
      <Marquee items={MARQUEE} />
      <StatsBand />
      <Challenges />
      <CoreServices />
      <IndustriesGrid />
      <Functions />
      <Software />
      <Why />
      <LogoSlider />
      <TestimonialSlider />
      <PortfolioPreview />
      <section className="bg-sand py-20 md:py-32" data-testid="process-section">
        <div className="container-x">
          <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <SectionHead eyebrow="Our working process" title="From Business Idea to Digital Solution" />
            <Reveal><Link to="/contact#inquiry" className="btn-ink shrink-0" data-testid="process-start-cta">Start Your Project <ArrowRight className="h-4 w-4" aria-hidden /></Link></Reveal>
          </div>
          <div className="mt-16"><ProcessSteps /></div>
        </div>
      </section>
      <WhoWeHelp items={WHO} />
      <section className="border-t border-ink/10 py-20 md:py-32" data-testid="faq-section">
        <div className="container-x grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHead eyebrow="FAQ" title="Frequently Asked Questions" />
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-ink/75">Pricing depends on the project scope, features, integrations and design requirements. Share your goals and we will recommend a sensible starting point.</p>
              <Link to="/contact#inquiry" className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-ink underline underline-offset-4">Ask a different question <ArrowRight className="h-4 w-4" aria-hidden /></Link>
            </Reveal>
          </div>
          <div className="lg:col-span-8"><FaqList faqs={HOME_FAQS} testId="home-faq" /></div>
        </div>
      </section>
      <section id="home-inquiry" className="bg-sand py-20 md:py-32" data-testid="home-inquiry-section">
        <div className="container-x grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHead eyebrow="Project inquiry" title="Tell Us About Your Project" intro="No sign-up needed. Share your details and our team will contact you." />
            <ol className="mt-10 space-y-6">
              {STEPS.map((s, i) => (
                <Reveal as="li" key={s.t} delay={i * 0.08} className="flex gap-4">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-ink text-lime"><s.icon className="h-4 w-4" aria-hidden /></span>
                  <div><h3 className="font-semibold text-ink">{s.t}</h3><p className="text-sm text-ink/70">{s.d}</p></div>
                </Reveal>
              ))}
            </ol>
          </div>
          <div className="lg:col-span-8"><InquiryForm /></div>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
